<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-content">
      <div class="modal-header">
        <h2>価格変更</h2>
        <button @click="$emit('close')" class="btn-close">&times;</button>
      </div>

      <div class="current-price">
        <label>現在の価格</label>
        <strong>¥{{ formatPrice(dataset.price_per_search) }}</strong>
      </div>

      <form @submit.prevent="handleSubmit">
        <div class="form-group">
          <label for="new_price">新しい価格（円） <span class="required">*</span></label>
          <input
            id="new_price"
            v-model.number="form.new_price"
            type="number"
            step="0.01"
            min="0"
            max="1000"
            required
            placeholder="0.00"
          />
          <small>0.00 = 無料、最大1000円まで</small>
        </div>

        <div class="form-group">
          <label for="reason">変更理由</label>
          <textarea
            id="reason"
            v-model="form.reason"
            rows="3"
            placeholder="価格変更の理由を入力してください（任意）"
          ></textarea>
        </div>

        <div v-if="error" class="error-message">
          {{ error }}
        </div>

        <div class="modal-footer">
          <button type="button" @click="$emit('close')" class="btn-secondary">
            キャンセル
          </button>
          <button type="submit" :disabled="loading" class="btn-primary">
            {{ loading ? '変更中...' : '変更' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useDatasetStore } from '../../stores/dataset.js'

const props = defineProps({
  dataset: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['close', 'changed'])
const datasetStore = useDatasetStore()

const form = ref({
  new_price: props.dataset.price_per_search,
  reason: '',
})

const loading = ref(false)
const error = ref(null)

function formatPrice(price) {
  return parseFloat(price).toFixed(2)
}

async function handleSubmit() {
  loading.value = true
  error.value = null

  try {
    await datasetStore.changePrice(
      props.dataset.dataset_id,
      form.value.new_price,
      form.value.reason
    )

    emit('changed')
  } catch (err) {
    error.value = err.response?.data?.error?.message || '価格の変更に失敗しました'
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #dee2e6;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
}

.btn-close {
  background: none;
  border: none;
  font-size: 2rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0;
  width: 2rem;
  height: 2rem;
  line-height: 1;
}

.btn-close:hover {
  color: #212529;
}

.current-price {
  padding: 1.5rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.current-price label {
  display: block;
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.current-price strong {
  font-size: 1.5rem;
  color: #28a745;
}

form {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.required {
  color: #dc3545;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 1rem;
  box-sizing: border-box;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.form-group small {
  display: block;
  margin-top: 0.25rem;
  color: #6c757d;
  font-size: 0.875rem;
}

.error-message {
  padding: 0.75rem;
  margin-bottom: 1rem;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  color: #721c24;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #dee2e6;
}

.btn-primary,
.btn-secondary {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0056b3;
}

.btn-primary:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #545b62;
}
</style>
